import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Wallet, Save, RefreshCw, Plus, Lock, Download, Loader2, CheckCircle, XCircle, SkipForward, Plug, Wifi, WifiOff } from "lucide-react";

interface BinanceEntry {
  id?: string;
  network: string;
  currency: string;
  address: string;
}

interface FetchResult {
  coin: string;
  network: string;
  networkLabel: string;
  address: string;
  status: string;
}

interface TestResult {
  vps_url: string;
  vps_secret: string;
  mode: string;
  outgoing_ip: string;
  whitelist_ip?: string;
  ip_verified?: boolean;
  resolved_vps_url?: string;
  health: { status: string; data?: any; error?: string };
  binance_api: { status: string; coins_count?: number; error?: string };
  deposit_address: { status: string; address?: string; error?: string };
}

const AdminBinanceSettings = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<BinanceEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [fetching, setFetching] = useState(false);
  const [fetchResults, setFetchResults] = useState<FetchResult[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResults, setTestResults] = useState<TestResult | null>(null);

  const [newNetwork, setNewNetwork] = useState("");
  const [newCurrency, setNewCurrency] = useState("");
  const [newAddress, setNewAddress] = useState("");

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("platform_settings")
      .select("id, setting_key, setting_value, description")
      .like("setting_key", "binance_address_%");

    const result: BinanceEntry[] = (data || []).map(d => {
      const parts = d.setting_key.replace("binance_address_", "").split("__");
      return {
        id: d.id,
        network: parts[0] || "",
        currency: parts[1] || "",
        address: d.setting_value || "",
      };
    });
    setEntries(result);
    setLoading(false);
  };

  const makeKey = (network: string, currency: string) =>
    `binance_address_${network.trim()}__${currency.trim().toUpperCase()}`;

  const handleAdd = async () => {
    if (!newNetwork.trim() || !newCurrency.trim() || !newAddress.trim()) {
      toast({ title: "خطأ", description: "أدخل الشبكة والعملة والعنوان", variant: "destructive" });
      return;
    }
    const key = makeKey(newNetwork, newCurrency);
    if (entries.some(e => makeKey(e.network, e.currency) === key)) {
      toast({ title: "خطأ", description: "هذه الشبكة والعملة موجودة بالفعل", variant: "destructive" });
      return;
    }
    setSaving("new");
    try {
      const { error } = await supabase.from("platform_settings").insert({
        setting_key: key,
        setting_value: newAddress.trim(),
        description: `عنوان Binance لـ ${newCurrency.trim().toUpperCase()} على شبكة ${newNetwork.trim()}`,
      });
      if (error) throw error;
      toast({ title: "تمت الإضافة وتم القفل نهائياً 🔒" });
      setNewNetwork("");
      setNewCurrency("");
      setNewAddress("");
      fetchSettings();
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(null);
    }
  };

  const handleAutoFetch = async () => {
    setFetching(true);
    setFetchResults(null);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || "";

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/fetch-binance-addresses`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "",
          },
          body: JSON.stringify({}),
        },
      );
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || "فشل الجلب");
      
      setFetchResults(data.results || []);
      toast({
        title: "تم جلب العناوين من Binance ✅",
        description: `محفوظ: ${data.saved} | موجود مسبقاً: ${data.skipped} | فشل: ${data.failed}`,
      });
      fetchSettings();
    } catch (e: any) {
      toast({ title: "خطأ في الجلب التلقائي", description: e.message, variant: "destructive" });
    } finally {
      setFetching(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResults(null);
    try {
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || "";

      const res = await fetch(
        `https://${projectId}.supabase.co/functions/v1/binance-withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
            "apikey": import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY || "",
          },
          body: JSON.stringify({ action: "test-connection" }),
        },
      );
      const data = await res.json();
      if (!res.ok || !data?.success) throw new Error(data?.error || "فشل الاختبار");
      
      setTestResults(data.results);
      const allOk = data.results.binance_api?.status?.includes("✅");
      toast({
        title: allOk ? "الاتصال يعمل بنجاح ✅" : "هناك مشاكل في الاتصال ⚠️",
        description: allOk ? "VPS و Binance API يعملان بشكل صحيح" : "راجع تفاصيل الاختبار أدناه",
        variant: allOk ? "default" : "destructive",
      });
    } catch (e: any) {
      toast({ title: "خطأ في اختبار الاتصال", description: e.message, variant: "destructive" });
    } finally {
      setTesting(false);
    }
  };

  const maskAddress = (addr: string) => {
    if (!addr || addr.length < 12) return addr;
    return addr.slice(0, 6) + "••••••" + addr.slice(-6);
  };

  const displayIp = testResults
    ? (testResults.ip_verified ? testResults.outgoing_ip : (testResults.whitelist_ip || ""))
    : "";

  const isIpVerified = Boolean(testResults?.ip_verified);

  if (loading) {
    return <Card className="glass"><CardContent className="p-6"><div className="animate-pulse text-muted-foreground">جاري التحميل...</div></CardContent></Card>;
  }

  return (
    <Card className="glass">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2 text-foreground">
            <Wallet className="w-5 h-5 text-primary" />
            عناوين Binance للسحب
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={handleTestConnection} disabled={testing}>
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plug className="w-4 h-4 mr-1" />}
              {testing ? "جاري الاختبار..." : "اختبار الاتصال"}
            </Button>
            <Button variant="default" size="sm" onClick={handleAutoFetch} disabled={fetching}>
              {fetching ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />}
              {fetching ? "جاري الجلب..." : "جلب تلقائي من Binance"}
            </Button>
            <Button variant="outline" size="sm" onClick={fetchSettings}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          أضف عنوان إيداع Binance لكل شبكة وعملة، أو استخدم زر "جلب تلقائي" لجلب جميع العناوين دفعة واحدة من Binance API.
          <span className="text-destructive font-semibold"> ⚠️ العناوين تُقفل نهائياً بعد الحفظ ولا يمكن تعديلها أو حذفها.</span>
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection test results */}
        {testResults && (
          <div className="p-4 rounded-lg border border-border bg-secondary/30 space-y-3">
            <p className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
              <Plug className="w-4 h-4" /> نتائج اختبار الاتصال:
            </p>
            {/* Outgoing IP Banner */}
            {displayIp && (
              <div className="p-3 rounded-lg border border-primary/50 bg-primary/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wifi className="w-5 h-5 text-primary" />
                  <div>
                    <p className="text-sm font-bold text-foreground">IP المطلوب إضافته في Binance</p>
                    <p className="text-xs text-muted-foreground">
                      {isIpVerified
                        ? "تم التحقق منه مباشرة من الخادم"
                        : "هذا هو الـ IP الثابت المهيأ للنظام حتى لو تعذر التحقق المباشر الآن"}
                    </p>
                    {!isIpVerified && testResults.outgoing_ip && (
                      <p className="text-[10px] text-muted-foreground mt-1">حالة التحقق: {testResults.outgoing_ip}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-lg font-bold font-mono text-primary bg-primary/20 px-3 py-1 rounded">{displayIp}</code>
                  <Button variant="outline" size="sm" onClick={() => {
                    navigator.clipboard.writeText(displayIp);
                    toast({ title: "تم النسخ ✅", description: displayIp });
                  }}>نسخ</Button>
                </div>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* VPS */}
              <div className={`p-3 rounded-lg border ${testResults.health?.status?.includes("✅") ? "border-green-500/50 bg-green-500/10" : "border-destructive/50 bg-destructive/10"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {testResults.health?.status?.includes("✅") ? <Wifi className="w-4 h-4 text-green-500" /> : <WifiOff className="w-4 h-4 text-destructive" />}
                  <span className="text-xs font-semibold">VPS</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {testResults.health?.status?.includes("✅") ? "متصل" : (testResults.health?.error || "غير متصل")}
                </p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  الوضع: {testResults.mode || "VPS (IP ثابت)"}
                </p>
                {testResults.resolved_vps_url && (
                  <p className="text-[10px] text-muted-foreground mt-1 break-all">
                    الرابط الفعلي: {testResults.resolved_vps_url}
                  </p>
                )}
              </div>

              {/* Binance API */}
              <div className={`p-3 rounded-lg border ${testResults.binance_api?.status?.includes("✅") ? "border-green-500/50 bg-green-500/10" : "border-destructive/50 bg-destructive/10"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {testResults.binance_api?.status?.includes("✅") ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-destructive" />}
                  <span className="text-xs font-semibold">Binance API</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {testResults.binance_api?.status?.includes("✅") 
                    ? `متصل (${testResults.binance_api.coins_count || 0} عملة)` 
                    : testResults.binance_api?.error || "فشل"}
                </p>
              </div>

              {/* Deposit Address */}
              <div className={`p-3 rounded-lg border ${testResults.deposit_address?.status?.includes("✅") ? "border-green-500/50 bg-green-500/10" : "border-destructive/50 bg-destructive/10"}`}>
                <div className="flex items-center gap-2 mb-1">
                  {testResults.deposit_address?.status?.includes("✅") ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-destructive" />}
                  <span className="text-xs font-semibold">عناوين الإيداع</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {testResults.deposit_address?.status?.includes("✅") 
                    ? `BTC: ${testResults.deposit_address.address}` 
                    : testResults.deposit_address?.error || "فشل"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <p className="text-[10px] text-muted-foreground">VPS: {testResults.vps_url} | Secret: {testResults.vps_secret}</p>
              <Button variant="ghost" size="sm" onClick={() => setTestResults(null)}>إغلاق</Button>
            </div>
          </div>
        )}

        {/* Auto-fetch results */}
        {fetchResults && (
          <div className="p-4 rounded-lg border border-border bg-secondary/30 space-y-2 max-h-60 overflow-y-auto">
            <p className="text-sm font-semibold text-foreground mb-2">نتائج الجلب التلقائي:</p>
            {fetchResults.map((r, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {r.status === "saved" && <CheckCircle className="w-3.5 h-3.5 text-green-500 shrink-0" />}
                {r.status === "exists" && <SkipForward className="w-3.5 h-3.5 text-yellow-500 shrink-0" />}
                {(r.status.startsWith("error") || r.status.startsWith("api_error")) && <XCircle className="w-3.5 h-3.5 text-destructive shrink-0" />}
                <span className="font-mono">{r.coin}/{r.network}</span>
                <span className="text-muted-foreground">({r.networkLabel})</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.status === "saved" ? "bg-green-500/20 text-green-400" : r.status === "exists" ? "bg-yellow-500/20 text-yellow-400" : "bg-destructive/20 text-destructive"}`}>
                  {r.status === "saved" ? "تم الحفظ" : r.status === "exists" ? "موجود مسبقاً" : "فشل"}
                </span>
                {r.address && <span className="font-mono text-muted-foreground">{r.address}</span>}
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setFetchResults(null)}>إغلاق</Button>
          </div>
        )}

        {/* Manual add */}
        <div className="p-4 rounded-lg border border-dashed border-primary/40 bg-primary/5 space-y-3">
          <p className="text-sm font-semibold text-foreground flex items-center gap-2"><Plus className="w-4 h-4" /> إضافة يدوية (شبكة وعملة جديدة)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              value={newNetwork}
              onChange={e => setNewNetwork(e.target.value)}
              placeholder="اسم الشبكة (مثل BSC, TRX, ETH)"
              dir="ltr"
              className="bg-input border-border text-sm"
            />
            <Input
              value={newCurrency}
              onChange={e => setNewCurrency(e.target.value)}
              placeholder="رمز العملة (مثل USDT, BNB, ETH)"
              dir="ltr"
              className="bg-input border-border text-sm"
            />
            <Input
              value={newAddress}
              onChange={e => setNewAddress(e.target.value)}
              placeholder="عنوان Binance للإيداع"
              dir="ltr"
              className="bg-input border-border font-mono text-xs"
            />
          </div>
          <Button size="sm" onClick={handleAdd} disabled={saving === "new"}>
            <Save className="w-4 h-4 mr-1" /> حفظ وقفل نهائي 🔒
          </Button>
        </div>

        {/* Existing entries */}
        {entries.length === 0 && (
          <p className="text-center text-muted-foreground text-sm py-4">لا توجد عناوين مضافة بعد. استخدم زر "جلب تلقائي من Binance" لجلب جميع العناوين.</p>
        )}

        {entries.length > 0 && (
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground mb-2">العناوين المحفوظة ({entries.length}):</p>
            {entries.map(entry => {
              const key = makeKey(entry.network, entry.currency);
              return (
                <div key={key} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/50 border border-yellow-500/30">
                  <Lock className="w-4 h-4 text-yellow-500 shrink-0" />
                  <div className="shrink-0 w-28">
                    <p className="text-xs text-muted-foreground">الشبكة</p>
                    <p className="text-sm font-medium text-foreground">{entry.network}</p>
                  </div>
                  <div className="shrink-0 w-16">
                    <p className="text-xs text-muted-foreground">العملة</p>
                    <p className="text-sm font-bold text-primary">{entry.currency}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground">العنوان (مقفل)</p>
                    <p className="text-sm font-mono text-foreground/70 truncate">{maskAddress(entry.address)}</p>
                  </div>
                  <span className="text-xs text-yellow-500 font-semibold shrink-0">🔒</span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminBinanceSettings;
